/**
 * FamilyTab — extracted from EmployeeCreate.js.
 * Receives state + handlers via `ctx` prop bag.
 */
import React from 'react';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Button } from '../../../../components/ui/button';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Textarea } from '../../../../components/ui/textarea';
import { TabsContent } from '../../../../components/ui/tabs';
import { Badge } from '../../../../components/ui/badge';
import { Plus, Trash2, User, Building2, Briefcase, Mail, Phone, Home, CreditCard, FileText, GraduationCap, Clock, DollarSign, Users, Heart, Calendar, Wallet, RefreshCw } from 'lucide-react';

const FamilyTab = ({ ctx }) => {
  const {
    formData,
    handleAddFamilyMember,
    handleFamilyMemberChange,
    handleRemoveFamilyMember,
  } = ctx;

  return (
          <TabsContent value="family">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-stone-700" />
                    Family Members / Dependents (Infotype 0021)
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddFamilyMember}>
                    <Plus className="w-4 h-4 mr-1" /> Add Family Member
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {formData.family_members.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No family members added yet.</p>
                    <p className="text-sm">Click "Add Family Member" to add dependents or beneficiaries.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {formData.family_members.map((member, index) => (
                      <div key={member._uid} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-medium text-gray-700">Family Member #{index + 1}</h4>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveFamilyMember(member._uid)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Relationship *</Label>
                            <select
                              value={member.relationship}
                              onChange={(e) => handleFamilyMemberChange(member._uid, 'relationship', e.target.value)}
                              className="w-full h-10 px-3 border rounded-md"
                            >
                              <option value="">Select</option>
                              <option value="SPOUSE">Spouse</option>
                              <option value="CHILD">Child</option>
                              <option value="PARENT">Parent</option>
                              <option value="SIBLING">Sibling</option>
                              <option value="OTHER">Other</option>
                            </select>
                          </div>
                          <div>
                            <Label>First Name *</Label>
                            <Input
                              value={member.first_name}
                              onChange={(e) => handleFamilyMemberChange(member._uid, 'first_name', e.target.value)}
                              placeholder="First Name"
                            />
                          </div>
                          <div>
                            <Label>Last Name *</Label>
                            <Input
                              value={member.last_name}
                              onChange={(e) => handleFamilyMemberChange(member._uid, 'last_name', e.target.value)}
                              placeholder="Last Name"
                            />
                          </div>
                          <div>
                            <Label>Date of Birth</Label>
                            <Input
                              type="date"
                              value={member.date_of_birth || ''}
                              onChange={(e) => handleFamilyMemberChange(member._uid, 'date_of_birth', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Gender</Label>
                            <select
                              value={member.gender || ''}
                              onChange={(e) => handleFamilyMemberChange(member._uid, 'gender', e.target.value)}
                              className="w-full h-10 px-3 border rounded-md"
                            >
                              <option value="">Select</option>
                              <option value="M">Male</option>
                              <option value="F">Female</option>
                              <option value="O">Other</option>
                            </select>
                          </div>
                          <div>
                            <Label>Phone Number</Label>
                            <Input
                              value={member.phone_number || ''}
                              onChange={(e) => handleFamilyMemberChange(member._uid, 'phone_number', e.target.value)}
                              placeholder="+91-XXXXXXXXXX"
                            />
                          </div>
                          <div className="flex items-center gap-4 col-span-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={member.is_dependent}
                                onChange={(e) => handleFamilyMemberChange(member._uid, 'is_dependent', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Is Dependent</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={member.is_emergency_contact}
                                onChange={(e) => handleFamilyMemberChange(member._uid, 'is_emergency_contact', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Emergency Contact</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={member.health_insurance_covered}
                                onChange={(e) => handleFamilyMemberChange(member._uid, 'health_insurance_covered', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Health Insurance</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={member.life_insurance_beneficiary}
                                onChange={(e) => handleFamilyMemberChange(member._uid, 'life_insurance_beneficiary', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Life Insurance Beneficiary</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
  );
};

export default FamilyTab;
